"use client";

import { useEffect, useState } from "react";
import { Leaf, Menu, Phone } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { Sheet, SheetContent, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
function DesktopMenuConsumer({
  scrolled
}: {
  scrolled: boolean;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-2">
        <div className="h-6 w-16 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-6 w-16 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-6 w-16 bg-muted/40 animate-pulse rounded-md" />
        <div className="h-6 w-16 bg-muted/40 animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-6 lg:gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className={`group relative !bg-transparent !px-0 !py-1 text-sm font-medium tracking-[0.05em] transition-colors duration-300 hover:!bg-transparent focus:!bg-transparent ${scrolled ? "text-foreground hover:!text-primary" : "text-foreground hover:!text-primary"}`}>
                <span>{item.label}</span>
                <span className={`absolute -bottom-1 left-0 h-px w-0 transition-all duration-300 group-hover:w-full ${scrolled ? "bg-primary" : "bg-primary"}`} />
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenuConsumer() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-4 mt-12">
        <div className="h-10 w-3/4 bg-primary-foreground/10 animate-pulse rounded-md" />
        <div className="h-10 w-2/3 bg-primary-foreground/10 animate-pulse rounded-md" />
        <div className="h-10 w-3/4 bg-primary-foreground/10 animate-pulse rounded-md" />
      </div>;
  }
  return <nav className="flex flex-col gap-6 mt-16 relative z-10">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="font-serif text-3xl font-light tracking-tight text-primary-foreground hover:text-primary transition-colors duration-300">
          {item.label}
        </Link>)}
    </nav>;
}
export default function MainNavigation() {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 20);
    };
    handleScroll();
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section data-section-id="290" id="main-navigation" className={`sticky top-0 z-50 w-full transition-all duration-500 bg-background text-foreground ${scrolled ? "bg-background/95 backdrop-blur-lg border-b border-border" : "bg-background border-b border-transparent"}`}>
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-10">
        <div className={`flex items-center justify-between transition-all duration-500 ${scrolled ? "h-16 md:h-20" : "h-16 md:h-20"}`}>
          {}
          <div className="flex items-center shrink-0">
            <Link to="/" aria-label="Elite Brushstrokes Home" className="flex items-center">
              <WvcLogo className="h-7 w-auto" />
            </Link>
          </div>

          {}
          <div className="hidden md:flex flex-1 justify-center">
            <MenuProvider menu_id="2">
              <DesktopMenuConsumer scrolled={scrolled} />
            </MenuProvider>
          </div>

          {}
          <div className="hidden md:flex items-center gap-5 shrink-0">
            <Link to="tel:+15552345678" className={`hidden lg:inline-flex font-mono text-xs tracking-wider transition-colors duration-300 text-foreground hover:text-primary`}>
              (555) 234-5678
            </Link>
            <Button asChild className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 px-5 h-10 text-sm font-medium tracking-[0.03em] shadow-md">
              <Link to="/GetAQuotePage">
                <Leaf className="size-3.5" />
                <span>Request Quote</span>
              </Link>
            </Button>
          </div>

          {}
          <div className="md:hidden flex items-center gap-2">
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <Button variant="ghost" size="icon" aria-label="Open menu" className={`!bg-transparent hover:!bg-transparent text-foreground`}>
                  <Menu className="size-6" />
                </Button>
              </SheetTrigger>
              <SheetContent side="right" className="!bg-secondary border-l border-secondary/40 w-full sm:max-w-md p-8 overflow-hidden">
                <SheetTitle className="sr-only">Navigation Menu</SheetTitle>

                {}
                <div className="absolute -bottom-10 -right-10 opacity-20 pointer-events-none">
                  <Leaf className="size-64 text-primary" strokeWidth={0.5} />
                </div>

                <div className="flex flex-col h-full relative z-10">
                  <div className="flex items-center justify-between">
                    <Link to="/" aria-label="Elite Brushstrokes Home">
                      <WvcLogo className="h-7 w-auto text-white" />
                    </Link>
                  </div>

                  <MenuProvider menu_id="2">
                    <MobileMenuConsumer />
                  </MenuProvider>

                  <div className="mt-auto pt-10 flex flex-col gap-5 relative z-10">
                    <Link to="tel:+15552345678" className="inline-flex items-center gap-2 font-mono text-xs tracking-wider text-primary-foreground/80 hover:text-primary-foreground transition-colors">
                      <Phone className="size-3.5" />
                      <span>(555) 234-5678</span>
                    </Link>
                    <Button asChild className="rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 h-11 px-5 text-sm font-medium tracking-[0.03em] w-full sm:w-auto">
                      <Link to="/GetAQuotePage">
                        <Leaf className="size-4" />
                        <span>Request Quote</span>
                      </Link>
                    </Button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </div>
    </section>;
}